import React, { Component } from 'react';
import './ProgressBar.scss';
import { UploadProgressNotifier } from '../helpers/uploadProgressHandler';
import classNames from 'classnames';

export class ProgressBar extends Component<Props, State> {
	/**
	 * this has to be kept in sync with the stylesheet variable
	 */
	private updateInterval = 100;
	private targetCompletePercentage = .8;
	private creepTargetPercentage = .95;
	/**
	 * what percentage of the remaining gap to advance
	 * each update interval when creeping
	 */
	private creepAdvancePercentage = .01;
	private completeProgressBarTransitionTime = '.5s';
	/**
	 * the amount of time we expect a request to take
	 * once it has finished uploading (on average)
	 */
	private estimatedRequestResponseTime = 300;
	private hasCompleted = false;
	private timer: number | null = null;
	private startedAt: Date = new Date();
	private estimatedCompleteAt: Date = new Date();
	private unsubscribeToProgress: (() => void) | null = null;
	
	constructor(props: Props) {
		super(props);
		this.state = { progress: 0 };
	}
	
	componentDidMount() {
		this.startedAt = new Date();
		this.estimatedCompleteAt = new Date(this.startedAt.getTime() + this.props.estimatedDuration);
		this.timer = window.setInterval(() => this.update(), this.updateInterval);
		
		if (this.props.uploadProgressNotifier) {
		    this.unsubscribeToProgress = this.props.uploadProgressNotifier.subscribe((progress) => {
		    	if (progress === 0)
		    		return;
		    	
		    	const now = new Date();
		    	const elapsedUploadTime = now.getTime() - this.startedAt.getTime();
		    	const totalUploadTime = elapsedUploadTime / progress;
		    	this.estimatedCompleteAt = new Date(this.startedAt.getTime() + totalUploadTime + this.estimatedRequestResponseTime);
			});
		}
	}
	
	componentWillUnmount() {
		this.cleanup();
	}
	
	render() {
		return (
			<div className={classNames('progress-bar__container', { 'progress-bar__container_small': this.props.barSize === 'small'})}>
				<div className="progress-bar__bar" style={this.getBarStyles()}></div>
			</div>
		)
	}

	private update() {
		if (this.hasCompleted)
			return;
		
		if (this.props.isComplete) {
			this.hasCompleted = true;
			this.setState({ progress: 1 });
			this.cleanup();
			return;
		}
		
		const now = new Date();
		if (now < this.estimatedCompleteAt) {
			const timeLeft = this.estimatedCompleteAt.getTime() - now.getTime();
			
			// +1 is to include the current tick being processed
			const ticksLeft = (timeLeft / this.updateInterval) + 1;
			const percentLeft = this.targetCompletePercentage - this.state.progress;
			const percentToAdvance = percentLeft / ticksLeft;
			
			this.setState({ progress: this.state.progress + percentToAdvance });
		} else {
			// we've missed our target completion so 
			// we are in 'creep ahead' mode
			const percentLeft = this.creepTargetPercentage - this.state.progress;
			this.setState({ progress: this.state.progress + (percentLeft * this.creepAdvancePercentage) });
		}
	}
	
	private getBarStyles() {
		const styles: any = {};
		
		// width
		const rounded = Math.round(this.state.progress * 1000) / 10;
		styles.width = `${rounded}%`;
		
		// finish out the rest of the progress bar at 
		// nice steady pace
		if (this.hasCompleted) {
			styles['transition'] = `width ${this.completeProgressBarTransitionTime} linear`;
		}
		
		return styles;
	}
	
	private cleanup() {
		if (this.timer != null) {
			clearInterval(this.timer);
			this.timer = null;
		}
		
		if (this.unsubscribeToProgress) {
			this.unsubscribeToProgress();
			this.unsubscribeToProgress = null;
		}
	}
}

interface Props {
	/**
	 * estimate of how the long the task will take
	 * in order to determine progress speed. it's best
	 * to estimate high here to have the spinner start
	 * slow and then speed up instead of vice versa.
	 */
	estimatedDuration: number;
	isComplete: boolean;
	uploadProgressNotifier?: UploadProgressNotifier;
	barSize?: 'small' | nundefined;
}

interface State {
    progress: number;
}
